import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import { FaSearch, FaPlus, FaEdit, FaUserShield, FaCheck, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../api/apiClient';
import { useAuth } from '../../contexts/AuthContext';

const defaultFormState = {
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  role: 2,
  status: 1,
  instructor_group_ids: []
};

const ManageAdmins = () => {
  const { user } = useAuth();
  const orgId = user?.organization_id;

  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [formData, setFormData] = useState(defaultFormState);
  const [formErrors, setFormErrors] = useState({});

  const orgGroups = useMemo(
    () => groups.filter((group) => group.organization_id && parseInt(group.organization_id, 10) === parseInt(orgId, 10)),
    [groups, orgId]
  );

  const adminInstructorRoles = useMemo(
    () => roles.filter((role) => {
      const value = parseInt(role.value, 10);
      return value === 2 || value > 2;
    }),
    [roles]
  );

  const parseInstructorGroupIds = (value) => {
    let raw = value;
    if (typeof raw === 'string') {
      try {
        raw = JSON.parse(raw);
      } catch {
        return [];
      }
    }
    if (!Array.isArray(raw)) return [];
    return raw.map((id) => parseInt(id, 10)).filter((id) => !Number.isNaN(id));
  };

  const getInstructorGroupNames = (adminRecord) => {
    const ids = parseInstructorGroupIds(adminRecord.instructor_group_ids);
    if (ids.length === 0) return [];
    return ids
      .map((id) => groups.find((group) => parseInt(group.id, 10) === id)?.name)
      .filter(Boolean);
  };

  const getRoleName = (roleValue) => {
    const match = roles.find((role) => parseInt(role.value, 10) === parseInt(roleValue, 10));
    if (match) return match.name;
    if (parseInt(roleValue, 10) === 2) return 'Org Admin';
    return 'Unknown';
  };

  const refetchAdmins = async () => {
    if (!orgId) return;
    try {
      const response = await api.organizations.getAdmins(orgId);
      const list = response?.data?.data || response?.data || [];
      setAdmins(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Error refreshing admins:', error);
      toast.error(error.response?.data?.message || 'Failed to refresh admins.');
    }
  };

  useEffect(() => {
    if (!orgId) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        const [adminsResponse, rolesResponse, groupsResponse] = await Promise.all([
          api.organizations.getAdmins(orgId),
          api.roles.list(),
          api.groups.list()
        ]);

        const list = adminsResponse?.data?.data || adminsResponse?.data || [];
        setAdmins(Array.isArray(list) ? list : []);
        setRoles(Array.isArray(rolesResponse.data?.data) ? rolesResponse.data.data : []);
        setGroups(Array.isArray(groupsResponse.data?.data) ? groupsResponse.data.data : []);
      } catch (error) {
        console.error('Error fetching admins:', error);
        toast.error(error.response?.data?.message || 'Failed to load admins.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [orgId]);

  const filteredAdmins = useMemo(() => {
    if (!Array.isArray(admins)) return [];

    const term = searchTerm.trim().toLowerCase();

    return admins.filter((adminRecord) => {
      const matchesSearch =
        !term ||
        adminRecord.first_name?.toLowerCase().includes(term) ||
        adminRecord.last_name?.toLowerCase().includes(term) ||
        adminRecord.email?.toLowerCase().includes(term);

      if (!matchesSearch) return false;

      if (filterStatus === 'all') return true;
      if (filterStatus === 'active') return adminRecord.status === 1 && !adminRecord.deleted;
      if (filterStatus === 'inactive') return adminRecord.status === 0 && !adminRecord.deleted;
      return true;
    });
  }, [admins, filterStatus, searchTerm]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (event) => {
    setFilterStatus(event.target.value);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    const nextValue = name === 'status' || name === 'role' ? parseInt(value, 10) : value;

    setFormData((prevState) => ({
      ...prevState,
      [name]: nextValue
    }));

    if (formErrors[name]) {
      setFormErrors((prevState) => ({
        ...prevState,
        [name]: ''
      }));
    }
  };

  const handleGroupToggle = (groupId) => {
    const id = groupId.toString();
    setFormData((prev) => {
      const exists = prev.instructor_group_ids.includes(id);
      return {
        ...prev,
        instructor_group_ids: exists
          ? prev.instructor_group_ids.filter((value) => value !== id)
          : [...prev.instructor_group_ids, id]
      };
    });
  };

  const resetForm = () => {
    setFormData(defaultFormState);
    setFormErrors({});
    setEditingAdmin(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleShowAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const handleShowEditModal = (adminRecord) => {
    setEditingAdmin(adminRecord);
    setFormData({
      first_name: adminRecord.first_name || '',
      last_name: adminRecord.last_name || '',
      email: adminRecord.email || '',
      password: '',
      role: adminRecord.role ?? 2,
      status: adminRecord.status ?? 1,
      instructor_group_ids: parseInstructorGroupIds(adminRecord.instructor_group_ids).map((id) => id.toString())
    });
    setFormErrors({});
    setShowModal(true);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.first_name.trim()) {
      errors.first_name = 'First name is required.';
    }

    if (!formData.last_name.trim()) {
      errors.last_name = 'Last name is required.';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required.';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address.';
    }

    if (!editingAdmin && !formData.password.trim()) {
      errors.password = 'Password is required for new users.';
    } else if (!editingAdmin && formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters.';
    }

    if (!formData.role) {
      errors.role = 'Role is required.';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveAdmin = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!orgId) {
      toast.error('Organization ID missing. Please refresh the page.');
      return;
    }

    const payload = {
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      email: formData.email.trim(),
      role: parseInt(formData.role, 10) || 2,
      status: parseInt(formData.status, 10),
      instructor_group_ids: formData.instructor_group_ids
        .map((id) => parseInt(id, 10))
        .filter((id) => !Number.isNaN(id))
    };

    if (formData.password.trim()) {
      payload.password = formData.password.trim();
    }

    setModalLoading(true);

    try {
      if (editingAdmin) {
        await api.organizations.updateAdmin(orgId, editingAdmin.id, payload);
        toast.success('Admin/Instructor updated successfully.');
      } else {
        await api.organizations.addAdmin(orgId, payload);
        toast.success('Admin/Instructor created successfully.');
      }

      await refetchAdmins();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving admin:', error);
      toast.error(error.response?.data?.message || 'Failed to save admin/instructor.');
    } finally {
      setModalLoading(false);
    }
  };

  const handleStatusToggle = async (adminRecord) => {
    if (!orgId) {
      toast.error('Organization ID missing. Please refresh the page.');
      return;
    }

    const nextStatus = adminRecord.status === 1 ? 0 : 1;

    try {
      await api.organizations.updateAdmin(orgId, adminRecord.id, { status: nextStatus });
      toast.success(`Admin/Instructor ${nextStatus === 1 ? 'activated' : 'deactivated'}.`);
      await refetchAdmins();
    } catch (error) {
      console.error('Error toggling admin status:', error);
      toast.error(error.response?.data?.message || 'Failed to update status.');
    }
  };

  const renderStatusBadge = (status) => {
    if (status === 1) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <FaCheck className="w-4 h-4" />
          Active
        </span>
      );
    }

    if (status === 0) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
          <FaTimes className="w-4 h-4" />
          Inactive
        </span>
      );
    }

    return <Badge bg="warning">Unknown</Badge>;
  };

  if (!orgId) {
    return (
      <div className="p-6 text-center text-gray-600">
        Organization context is missing. Please sign in again.
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      <div className="mb-4">
        <h5 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
          Manage Admin/Instructor
        </h5>
      </div>

      <Row className="mb-3 g-2 sm:g-3">
        <Col xs={12} md={6} className="mb-2 mb-md-0">
          <div className="flex shadow-sm rounded-lg sm:rounded-xl overflow-hidden bg-white">
            <div className="flex items-center px-3 bg-white border-r border-gray-200">
              <FaSearch className="w-4 h-4 text-purple-600" />
            </div>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full px-3 py-2 border-0 focus:ring-2 focus:ring-purple-500 text-sm sm:text-base outline-none"
            />
          </div>
        </Col>
        <Col xs={12} sm={6} md={3} className="mb-2 mb-md-0">
          <Form.Select
            value={filterStatus}
            onChange={handleFilterChange}
            className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Form.Select>
        </Col>
        <Col xs={12} sm={6} md={3} className="text-end">
          <Button
            bsPrefix="custom-hamburger"
            onClick={handleShowAddModal}
            className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
          >
            <FaUserShield className="w-4 h-4" />
            <span className="whitespace-nowrap">Add Admin/Instructor</span>
          </Button>
        </Col>
      </Row>

      <Card className="border border-purple-100 bg-white shadow-lg rounded-xl sm:rounded-2xl overflow-hidden">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status" className="text-purple-600">
                <span className="visually-hidden">Loading admins...</span>
              </Spinner>
            </div>
          ) : filteredAdmins.length === 0 ? (
            <div className="text-center py-6 sm:py-8 bg-white/80 backdrop-blur shadow-md border border-purple-100 rounded-xl sm:rounded-2xl">
              <FaUserShield className="text-purple-400 mb-3" style={{ fontSize: '2rem' }} />
              <p className="text-gray-600 mb-0 text-sm sm:text-base">
                {searchTerm ? 'No admin/instructor users match your search.' : 'No admin/instructor users have been added yet.'}
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="m-0 min-w-max text-xs sm:text-sm">
                <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  <tr>
                    <th className="py-2 px-3">Name</th>
                    <th className="py-2 px-3">Email</th>
                    <th className="py-2 px-3">Role</th>
                    <th className="py-2 px-3">Groups</th>
                    <th className="py-2 px-3">Status</th>
                    <th className="py-2 px-3 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAdmins.map((adminRecord) => {
                    const groupNames = getInstructorGroupNames(adminRecord);
                    return (
                      <tr key={adminRecord.id} className="border-b border-purple-50 hover:bg-purple-50/50 transition-all">
                        <td className="py-2 px-3 align-middle">
                          <div className="fw-semibold text-gray-800 text-sm">
                            {adminRecord.first_name} {adminRecord.last_name}
                          </div>
                        </td>
                        <td className="py-2 px-3 align-middle text-gray-600 text-sm">{adminRecord.email}</td>
                        <td className="py-2 px-3 align-middle">
                          <Badge bg="warning" className="px-2 py-1 text-xs">{getRoleName(adminRecord.role)}</Badge>
                        </td>
                        <td className="py-2 px-3 align-middle">
                          {groupNames.length === 0 ? (
                            <span className="text-gray-400 text-xs">—</span>
                          ) : (
                            <div className="d-flex flex-wrap gap-1">
                              {groupNames.map((name) => (
                                <Badge key={name} bg="info" className="px-2 py-1 text-xs">{name}</Badge>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-3 align-middle">{renderStatusBadge(adminRecord.status)}</td>
                        <td className="py-2 px-3 align-middle">
                          <div className="d-flex justify-content-end gap-2">
                            <Button
                              bsPrefix="custom-hamburger"
                              onClick={() => handleShowEditModal(adminRecord)}
                              className="border-0 border-purple-300 text-purple-600 rounded hover:bg-purple-50 transition p-1"
                              title="Edit admin/instructor"
                            >
                              <FaEdit className="w-5 h-5" />
                            </Button>
                            <Button
                              bsPrefix="custom-hamburger"
                              onClick={() => handleStatusToggle(adminRecord)}
                              className={`border-0 rounded transition p-1 ${adminRecord.status === 1 ? 'text-yellow-600 hover:bg-yellow-50' : 'text-green-600 hover:bg-green-50'}`}
                              title={adminRecord.status === 1 ? 'Deactivate' : 'Activate'}
                            >
                              {adminRecord.status === 1 ? <FaTimes className="w-5 h-5" /> : <FaCheck className="w-5 h-5" />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            {editingAdmin ? 'Edit Admin/Instructor' : 'Add Admin/Instructor'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-3 sm:p-5">
          <Form onSubmit={handleSaveAdmin}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">First Name <span className="text-red-500">*</span></label>
                <input
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 rounded-lg border ${formErrors.first_name ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none text-sm`}
                  placeholder="Enter first name"
                />
                {formErrors.first_name && <p className="text-red-500 text-xs mt-1">{formErrors.first_name}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Last Name <span className="text-red-500">*</span></label>
                <input
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 rounded-lg border ${formErrors.last_name ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none text-sm`}
                  placeholder="Enter last name"
                />
                {formErrors.last_name && <p className="text-red-500 text-xs mt-1">{formErrors.last_name}</p>}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
              <input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={Boolean(editingAdmin)}
                readOnly={Boolean(editingAdmin)}
                className={`w-full px-3 py-2 rounded-lg border ${formErrors.email ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none text-sm ${editingAdmin ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                placeholder="Enter email address"
              />
              {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
              {editingAdmin && <p className="text-xs text-gray-500 mt-1">Email cannot be changed for an existing user.</p>}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Password {!editingAdmin && <span className="text-red-500">*</span>}
                {editingAdmin && <span className="text-gray-500 text-xs"> (leave blank to keep current)</span>}
              </label>
              <input
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 rounded-lg border ${formErrors.password ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none text-sm`}
                placeholder={editingAdmin ? 'Enter new password (optional)' : 'Enter password'}
                minLength={6}
              />
              {formErrors.password && <p className="text-red-500 text-xs mt-1">{formErrors.password}</p>}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Role <span className="text-red-500">*</span></label>
              <select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 rounded-lg border ${formErrors.role ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none text-sm`}
              >
                {adminInstructorRoles.length > 0 ? (
                  adminInstructorRoles.map((role) => (
                    <option key={role.id} value={role.value}>
                      {role.name}
                    </option>
                  ))
                ) : (
                  <>
                    <option value={2}>Org Admin</option>
                    <option value={3}>Instructor</option>
                  </>
                )}
              </select>
              {formErrors.role && <p className="text-red-500 text-xs mt-1">{formErrors.role}</p>}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Organization Groups <span className="text-gray-500 text-xs font-normal">(optional)</span>
              </label>
              <div className="border border-purple-200 rounded-lg p-3 bg-white max-h-48 overflow-y-auto">
                {orgGroups.length === 0 ? (
                  <p className="text-muted text-sm mb-0">No groups linked to this organization.</p>
                ) : (
                  orgGroups.map((group) => {
                    const groupId = group.id.toString();
                    return (
                      <Form.Check
                        key={group.id}
                        type="checkbox"
                        id={`org-admin-group-${groupId}`}
                        label={group.name}
                        checked={formData.instructor_group_ids.includes(groupId)}
                        onChange={() => handleGroupToggle(groupId)}
                        className="mb-2"
                      />
                    );
                  })
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">Only organization groups are listed.</p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>

            <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
              <Row className="w-100 g-3">
                <Col md={6}>
                  <Button bsPrefix="custom-hamburger" onClick={handleCloseModal} disabled={modalLoading} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                    Cancel
                  </Button>
                </Col>
                <Col md={6}>
                  <Button
                    bsPrefix="custom-hamburger"
                    type="submit"
                    disabled={modalLoading}
                    className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
                  >
                    {modalLoading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        {editingAdmin ? 'Saving...' : 'Creating...'}
                      </>
                    ) : (
                      editingAdmin ? 'Update Admin/Instructor' : 'Create Admin/Instructor'
                    )}
                  </Button>
                </Col>
              </Row>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ManageAdmins;
